import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getPokemonImageUrl } from '../../utils/imageUtils';

const EventCard = ({ event, variant = 'general', compact = false }) => {
  const navigate = useNavigate();
  
  // Safety check - reject category objects IMMEDIATELY
  if (!event || typeof event !== 'object' || Array.isArray(event)) {
    console.warn('EventCard: Invalid event prop', typeof event);
    return null;
  }
  
  // Handle click to navigate to detail page
  const handleClick = () => {
    if (event.id) {
      navigate(`/events/${event.id}`);
    }
  };
  
  // Check for category object indicators - must check BEFORE any rendering
  // Category objects have: has_bonuses, default_start_time, is_weekly, day_of_week, is_monthly, is_multi_day
  // AND they DON'T have: start_date, end_date, category, sub_category
  // Note: Events CAN have name_en/name_th and description_en/description_th (they're event fields too)
  const hasCategoryFields = event.has_bonuses !== undefined || 
                            event.default_start_time !== undefined || 
                            event.is_weekly !== undefined || 
                            event.day_of_week !== undefined ||
                            event.is_monthly !== undefined || 
                            event.is_multi_day !== undefined;
  
  const hasEventFields = event.start_date !== undefined || 
                        event.end_date !== undefined || 
                        event.category !== undefined ||
                        event.sub_category !== undefined;
  
  // If it has category-specific fields but NO event fields, it's a category object
  if (hasCategoryFields && !hasEventFields) {
    console.warn('EventCard: Rejected category object with keys:', Object.keys(event));
    return null;
  }
  
  // Must have at least one event field
  if (!event.start_date && !event.title && !event.title_th && !event.name_en && !event.name_th && !event.id) {
    console.warn('EventCard: Event missing required fields');
    return null;
  }
  
  // Helper to get event title (prefer name_en/name_th, fallback to title/title_th)
  const getEventTitle = () => {
    return event.name_en || event.name_th || event.title || event.title_th || 'ไม่ระบุชื่อ';
  };
  
  // Helper to get event description (prefer description_en/description_th, fallback to description)
  const getEventDescription = () => {
    return event.description_en || event.description_th || event.description || null;
  };

  // Get event status
  const getEventStatus = () => {
    const now = new Date();
    const startDate = event.start_date ? new Date(event.start_date) : null;
    const endDate = event.end_date ? new Date(event.end_date) : null;

    if (!startDate || isNaN(startDate.getTime())) {
      return { type: 'unknown', label: '', isLive: false, isPast: false };
    }

    if (!endDate || isNaN(endDate.getTime())) {
      if (now < startDate) {
        return { type: 'upcoming', label: '', isLive: false, isPast: false };
      }
      return { type: 'live', label: 'LIVE', isLive: true, isPast: false };
    }

    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);

    if (now > endOfDay) {
      return { type: 'past', label: 'จบแล้ว', isLive: false, isPast: true };
    } else if (now >= startDate && now <= endOfDay) {
      return { type: 'live', label: 'LIVE', isLive: true, isPast: false };
    } else {
      return { type: 'upcoming', label: '', isLive: false, isPast: false };
    }
  };

  // Format date range
  const formatDateRange = () => {
    if (!event.start_date || !event.end_date) return 'ไม่ระบุ';

    try {
      const start = new Date(event.start_date);
      const end = new Date(event.end_date);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return `${event.start_date} - ${event.end_date}`;
      }

      const startMonth = start.toLocaleDateString('th-TH', { month: 'short' });
      const endMonth = end.toLocaleDateString('th-TH', { month: 'short' });
      const startDay = start.getDate();
      const endDay = end.getDate();

      if (start.getTime() === end.getTime()) {
        return `${startDay} ${startMonth}`;
      } else if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${startDay} - ${endDay} ${startMonth}`;
      } else if (start.getFullYear() === end.getFullYear()) {
        return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
      } else {
        return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${end.getFullYear()}`;
      }
    } catch (e) {
      return `${event.start_date} - ${event.end_date}`;
    }
  };

  // Get event image
  const getEventImage = () => {
    if (event.image_url) return event.image_url;
    if (event.pokemon_id) {
      return getPokemonImageUrl(event.pokemon_id);
    }
    return '/images/pokemon/0000.webp';
  };

  const status = getEventStatus();

  // Raid Event Card
  if (variant === 'raid') {
    const subCategory = event.sub_category || 'none';
    let borderColor = '#3B82F6';
    if (subCategory === 'mega') borderColor = '#F97316'; // Orange
    else if (subCategory === '5-star') borderColor = '#6366F1'; // Indigo
    else if (subCategory === 'shadow') borderColor = '#9333EA'; // Purple

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.05, y: -4 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleClick}
        className={`event-card group bg-white rounded-2xl p-4 shadow-sm hover:shadow-lg transition-all duration-300 border-b-4 relative cursor-pointer ${
          status.isPast ? 'grayscale opacity-60' : ''
        }`}
        style={{ borderColor }}
      >
        {/* Sub-category Badge */}
        {subCategory !== 'none' && (
          <span className={`absolute top-3 right-3 text-xs font-bold px-2 py-0.5 rounded ${
            subCategory === 'mega' ? 'text-orange-600 bg-orange-100' :
            subCategory === '5-star' ? 'text-indigo-600 bg-indigo-100' :
            subCategory === 'shadow' ? 'text-purple-700 bg-purple-200' :
            'text-gray-600 bg-gray-100'
          }`}>
            {subCategory === 'mega' ? 'MEGA' : subCategory === '5-star' ? '5 STAR' : subCategory === 'shadow' ? 'SHADOW' : subCategory.toUpperCase()}
          </span>
        )}

        {/* Status Badges */}
        {status.isLive && (
          <div className="absolute -top-1 -left-1 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-lg z-20 animate-pulse">
            LIVE
          </div>
        )}
        {status.isPast && (
          <div className="absolute top-2 left-2 bg-gray-500 text-white text-[10px] px-2 py-1 rounded font-bold z-20">
            {status.label}
          </div>
        )}

        {/* Shadow background for shadow raids */}
        {subCategory === 'shadow' && (
          <div className="absolute inset-0 bg-purple-50 opacity-50 z-0 rounded-2xl"></div>
        )}

        <div className="flex flex-col items-center pt-2 relative z-10">
          <img
            src={getEventImage()}
            alt={getEventTitle()}
            className={`w-48 h-48 md:w-52 md:h-52 object-contain group-hover:scale-110 transition-transform duration-300 drop-shadow-md mt-2 ${
              subCategory === 'shadow' ? 'filter brightness-90 contrast-125' : ''
            }`}
            onError={(e) => {
              e.target.src = '/images/pokemon/0000.webp';
            }}
          />
          <h3 className="text-lg font-bold mt-3 text-gray-800 text-center">
            {getEventTitle()}
          </h3>
          {getEventDescription() && (
            <p className="text-xs text-gray-400 mb-2">({getEventDescription()})</p>
          )}
          <p className="text-sm text-gray-500 font-medium bg-gray-100 px-3 py-1 rounded-full mt-2">
            {formatDateRange()}
          </p>
        </div>
      </motion.div>
    );
  }

  // Spotlight Event Card (horizontal, compact)
  if (variant === 'spotlight') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleClick}
        className={`event-card flex items-center bg-white p-4 rounded-xl shadow-sm border border-green-100 hover:shadow-md transition-all relative cursor-pointer ${
          status.isPast ? 'grayscale opacity-60' : ''
        }`}
      >
        {/* Date Badge */}
        {event.start_date && (
          <div className="flex-shrink-0 w-14 text-center bg-green-50 rounded-lg py-2">
            <span className="block text-xl font-bold text-green-700">
              {new Date(event.start_date).getDate()}
            </span>
            <span className="text-[10px] text-green-600 uppercase font-bold">
              {new Date(event.start_date).toLocaleDateString('th-TH', { month: 'short' }).toUpperCase()}
            </span>
          </div>
        )}

        <img
          src={getEventImage()}
          alt={getEventTitle()}
          className="w-20 h-20 md:w-24 md:h-24 object-contain mx-4"
          onError={(e) => {
            e.target.src = '/images/pokemon/0000.webp';
          }}
        />

        <div className="flex-1">
          <h4 className="text-lg font-bold text-gray-800">{getEventTitle()}</h4>
          {event.bonus_text && (
            <div className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1">
              <span>↑</span> {typeof event.bonus_text === 'string' ? event.bonus_text : JSON.stringify(event.bonus_text)}
            </div>
          )}
        </div>

        {status.isLive && (
          <div className="absolute -top-1 -left-1 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-lg z-20 animate-pulse">
            LIVE
          </div>
        )}
        {status.isPast && (
          <div className="absolute top-2 left-2 bg-gray-500 text-white text-[10px] px-2 py-1 rounded font-bold z-20">
            {status.label}
          </div>
        )}
      </motion.div>
    );
  }

  // Max Monday Event Card (compact square)
  if (variant === 'max_monday') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.05, y: -4 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleClick}
        className="event-card bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col items-center text-center hover:shadow-md transition-all cursor-pointer"
      >
        {event.start_date && (
          <span className="text-sm font-bold text-gray-400 mb-1">
            {new Date(event.start_date).getDate()} {new Date(event.start_date).toLocaleDateString('th-TH', { month: 'short' })}
          </span>
        )}
        <img
          src={getEventImage()}
          alt={getEventTitle()}
          className="w-24 h-24 md:w-28 md:h-28 object-contain"
          onError={(e) => {
            e.target.src = '/images/pokemon/0000.webp';
          }}
        />
        <span className="font-bold text-gray-800 mt-2">{getEventTitle()}</span>
      </motion.div>
    );
  }

  // General Event Card (compact, date number + icon)
  const categoryColor = event.custom_color || '#3B82F6';
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
      className={`event-card bg-white rounded-xl p-5 shadow-sm border-l-4 hover:shadow-lg transition-all relative cursor-pointer ${
        status.isPast ? 'grayscale opacity-60' : ''
      }`}
      style={{ borderColor: categoryColor }}
    >
      <div className="flex justify-between items-start mb-2">
        {event.start_date && (
          <span className="text-2xl font-bold" style={{ color: categoryColor }}>
            {new Date(event.start_date).getDate()}
          </span>
        )}
        <img
          src={getEventImage()}
          alt={getEventTitle()}
          className="w-12 h-12 -mt-2 object-contain"
          onError={(e) => {
            e.target.src = '/images/pokemon/0000.webp';
          }}
        />
      </div>
      <h3 className="font-bold text-gray-800 leading-tight">{getEventTitle()}</h3>
      {getEventDescription() && (
        <p className="text-sm text-gray-500 mt-1">{getEventDescription()}</p>
      )}
      {status.isLive && (
        <div className="absolute -top-1 -left-1 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-lg z-20 animate-pulse">
          LIVE
        </div>
      )}
    </motion.div>
  );
};

export default EventCard;
